import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { api } from "@/api";
import { resolveMediaUrl } from "@/utils/helpers";

// Common Components
import { Navigation, Footer } from "@/components/common";

// Page Components
import { HomePage, SportPage, LeaguePage, FixturesPage, FindGamePage, PartnersPage, RegionPage, WatchPage, LivePage, SearchPage } from "@/components/pages";
import AllGamesPage from "@/components/pages/AllGamesPage";

// Admin Components
import { AdminPanel, VerifyEmail, ResetPassword, SetupPassword } from "@/components/admin/AdminComponents";

function App() {
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sportsRes, leaguesRes, settingsRes] = await Promise.all([
          api.getSports(),
          api.getLeagues(),
          api.getSettings()
        ]);
        setSports(sportsRes.data);
        setLeagues(leaguesRes.data);
        
        // Load favicon if configured
        const faviconUrl = settingsRes.data?.favicon_url;
        if (faviconUrl) {
          const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
          link.rel = 'icon';
          link.href = resolveMediaUrl(faviconUrl);
          document.head.appendChild(link);
        }
        
        // Load Google Analytics if configured
        const gaId = settingsRes.data?.google_analytics_id;
        if (gaId && gaId.startsWith('G-')) {
          // Load gtag.js
          const script1 = document.createElement('script');
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(script1);
          
          // Initialize gtag
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', gaId);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  return (
    <HelmetProvider>
      <div className="App bg-main min-h-screen flex flex-col">
        <BrowserRouter>
          <Navigation sports={sports} leagues={leagues} />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage sports={sports} />} />
              <Route path="/sport/:slug" element={<SportPage sports={sports} />} />
              <Route path="/region/:slug" element={<RegionPage leagues={leagues} />} />
              <Route path="/league/:slug" element={<LeaguePage leagues={leagues} />} />
              <Route path="/watch/:gameId" element={<WatchPage />} />
              <Route path="/live" element={<LivePage />} />
              <Route path="/games/:type" element={<AllGamesPage />} />
              <Route path="/fixtures" element={<FixturesPage sports={sports} />} />
              <Route path="/find-game" element={<FindGamePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/setup" element={<SetupPassword />} />
              <Route path="/admin/*" element={<AdminPanel sports={sports} />} />
              {/* Catch-all: redirect unknown routes (incl. removed /news /podcast /radio) to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
